
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Play, ArrowRight, Users, TrendingUp, Heart, Award, Target, CheckCircle, Calendar, Gift, PartyPopper, MessageCircle } from "lucide-react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import AwardsSection from "@/components/trust/AwardsSection";

const CelebrateCultureMoments = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <ProductPageLayout
      title="Celebrate Culture Moments - Milestone & Anniversary Platform"
      description="Create memorable moments that matter with Assembly's milestone celebration platform. Build stronger culture through meaningful recognition of anniversaries, achievements, and special occasions."
      keywords="milestone celebration, work anniversaries, culture moments, employee milestones, anniversary recognition"
      ctaTitle="Ready to Celebrate What Matters?"
      ctaDescription="See how Assembly helps companies create unforgettable milestone moments that strengthen culture and connection."
      ctaButtonText="Start Free Trial"
      ctaVariant="blue"
      showCTA={false}
    >
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <Badge className="mb-6 bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-200 text-sm font-medium px-4 py-2">
                MILESTONE CELEBRATIONS
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Celebrate Culture
                <br />
                <span className="text-orange-600">Moments</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg leading-relaxed">
                Turn work anniversaries, achievements, and special moments into memorable 
                celebrations that strengthen your culture and show people they matter.
              </p>
              <div className="flex justify-start">
                <Button 
                  size="lg" 
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-medium text-lg"
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Book Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* Milestone Card Mockup */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 relative max-w-md ml-auto">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PartyPopper className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-xl">🎉 5 Year Anniversary!</h3>
                  <p className="text-gray-500 text-lg">Alex Chen</p>
                </div>
                
                {/* Message */}
                <p className="text-gray-800 mb-6 leading-relaxed text-base text-center">
                  "Five years of incredible contributions! Thank you for your dedication, 
                  innovation, and the positive energy you bring every day."
                </p>
                
                {/* Achievement Badges */}
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="bg-orange-100 text-orange-800 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Innovator
                  </div>
                  <div className="bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Mentor
                  </div>
                </div>

                {/* Team celebration */}
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <div className="flex -space-x-2">
                    <img src="https://images.unsplash.com/photo-1649972904349-6e44c42644a7?w=32&h=32&fit=crop&crop=face" alt="Team member" className="w-8 h-8 rounded-full border-2 border-white" />
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face" alt="Team member" className="w-8 h-8 rounded-full border-2 border-white" />
                    <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face" alt="Team member" className="w-8 h-8 rounded-full border-2 border-white" />
                  </div>
                  <span>Team celebration in progress!</span>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-pink-400 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                  <Heart className="h-6 w-6 text-white fill-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <Gift className="h-5 w-5 text-white" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Logos Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12 sm:mb-16">
            <motion.h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6" {...fadeInUp}>
              You'll be in good company.
            </motion.h2>
            <motion.p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto" {...fadeInUp}>Many top organizations leverage Assembly as a Digital HQ to support their transition to hybrid work and nurture a positive workplace culture.</motion.p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center opacity-60">
            {[{
            name: "StubHub",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393801e8e212d1d42ff_stubhub-gray.svg"
          }, {
            name: "1-800-Flowers",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/6386539332627ece447341d4_1800flowers-gray.svg"
          }, {
            name: "Coca-Cola",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/638653928ded58181120ad86_Coca-Cola-gray.svg"
          }, {
            name: "Amazon",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393cd30f0e52523af12_Amazon-gray.svg"
          }, {
            name: "PODS",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393ed49964f0f60c3c7_PODS-gray.svg"
          }, {
            name: "Allbirds",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393708a2292ab2159f4_Allbirds-gray.svg"
          }].map((logo, index) => <motion.div key={index} className="flex items-center justify-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }}>
                <img src={logo.src} alt={`${logo.name} logo`} className="h-8 w-auto object-contain" />
              </motion.div>)}
          </div>
          
          <div className="mt-12 sm:mt-16">
            <AwardsSection />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-orange-600">92%</div>
              <div className="text-gray-600">Feel more valued</div>
            </motion.div>
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-yellow-600">78%</div>
              <div className="text-gray-600">Stronger connections</div>
            </motion.div>
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-pink-600">65%</div>
              <div className="text-gray-600">Better retention</div>
            </motion.div>
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-purple-600">5x</div>
              <div className="text-gray-600">More memorable</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem vs Solution */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why Milestone Moments Get Forgotten
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Don't let important moments slip by unnoticed. Here's how Assembly ensures every milestone becomes a meaningful celebration.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div 
              className="bg-red-50 border-2 border-red-200 rounded-2xl p-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">😔</span>
                </div>
                <h3 className="text-2xl font-bold text-red-800">Forgotten Milestones</h3>
              </div>
              <ul className="space-y-4 text-red-700">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Anniversaries slip by unnoticed</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Generic email reminders</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">No team participation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Impersonal celebrations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Missed opportunities for connection</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-green-50 border-2 border-green-200 rounded-2xl p-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎉</span>
                </div>
                <h3 className="text-2xl font-bold text-green-800">Assembly's Celebrations</h3>
              </div>
              <ul className="space-y-4 text-green-700">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Automated milestone tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Personalized celebration pages</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Team-wide participation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Meaningful recognition & rewards</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Lasting memory creation</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Celebrations That Create Connection
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Turn every milestone into a moment that strengthens your culture and shows people they truly matter.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Smart Milestone Tracking</h3>
              <p className="text-gray-600 leading-relaxed">
                Never miss an anniversary or achievement. Automatic tracking ensures every important moment gets celebrated.
              </p>
            </motion.div>

            <motion.div 
              className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Team Celebrations</h3>
              <p className="text-gray-600 leading-relaxed">
                Bring the whole team together to celebrate. Create shared moments that build stronger connections.
              </p>
            </motion.div>

            <motion.div 
              className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Gift className="h-8 w-8 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Personalized Rewards</h3>
              <p className="text-gray-600 leading-relaxed">
                Make celebrations meaningful with personalized rewards and recognition that reflects individual achievements.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Effortless Milestone Magic
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Create memorable celebrations that happen automatically, so no special moment gets missed.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Auto-Track Milestones</h3>
              <p className="text-gray-600 leading-relaxed">
                Set up once and forget. Assembly automatically tracks work anniversaries, project completions, and achievements.
              </p>
            </motion.div>

            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Celebrate Together</h3>
              <p className="text-gray-600 leading-relaxed">
                Teams get notified to join the celebration. Everyone can contribute messages, reactions, and recognition.
              </p>
            </motion.div>

            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-20 h-20 bg-pink-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Create Memories</h3>
              <p className="text-gray-600 leading-relaxed">
                Every celebration becomes a lasting memory with photos, messages, and achievements that people treasure.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Automatic Celebrations, Zero Admin Work
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Connect your HRIS and communication tools so milestone celebrations happen seamlessly without manual tracking
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto">
            <motion.div 
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">HRIS Milestone Sync</h3>
              </div>
              <p className="text-gray-600 mb-6 text-center">
                Automatically track employee milestones from your HR system - no manual date entry required
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-gray-700">Work anniversary tracking</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-gray-700">Birthday celebrations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-gray-700">Role change recognition</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Team Communication</h3>
              </div>
              <p className="text-gray-600 mb-6 text-center">
                Celebrations appear directly in Slack and Teams where your team naturally gathers
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-yellow-600" />
                  <span className="text-gray-700">Slack celebration posts</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-yellow-600" />
                  <span className="text-gray-700">Teams milestone notifications</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-yellow-600" />
                  <span className="text-gray-700">Company-wide announcements</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Photos Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Moments That Matter
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              See how teams around the world celebrate their people and build stronger cultures.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="relative rounded-2xl overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=300&fit=crop" 
                alt="Team celebration" 
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="font-bold text-lg">Anniversary Celebrations</h3>
                  <p className="text-sm opacity-90">Honoring years of dedication</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative rounded-2xl overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=300&fit=crop" 
                alt="Achievement recognition" 
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="font-bold text-lg">Achievement Moments</h3>
                  <p className="text-sm opacity-90">Celebrating big wins together</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative rounded-2xl overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=300&fit=crop" 
                alt="Team milestones" 
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="font-bold text-lg">Team Milestones</h3>
                  <p className="text-sm opacity-90">Building lasting connections</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-yellow-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Never Miss Another Milestone
            </h2>
            <p className="text-orange-100 text-xl mb-8 max-w-3xl mx-auto">
              Join thousands of companies using Assembly to create meaningful celebrations that build stronger cultures and deeper connections.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-orange-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => setIsDemoModalOpen(true)}
            >
              Book Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </ProductPageLayout>
  );
};

export default CelebrateCultureMoments;
